import { useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { CallRoute } from '@weave/schema-gen-ts/dist/schemas/phone/callroute/v1/callroute_service.pb';
import { useTranslation } from '@frontend/i18n';
import { formatPhoneNumber } from '@frontend/phone-numbers';
import { useAppScopeStore } from '@frontend/scope';
import { theme } from '@frontend/theme';
import { Modal, Text, TextField, useForm, DropdownField, CheckboxField } from '@frontend/design-system';
import { usePhoneSettingsShallowStore } from '../../../store/settings';
import { trackingId } from '../../../tracking';
import { PhoneNumber } from '../../../views/settings/types';
import { logError } from '../helpers';

export const UNASSIGNED_CALL_ROUTE_ID = 'unassign';

export const ReassignPhoneNumber = ({
  remainingCount,
  currentPhoneNumber,
  callRouteName,
  callRoutes,
  remainingPhoneNumbers = [],
  isLoading,
  handleReassignPhoneNumber,
  onClose,
}: {
  remainingCount: number;
  callRouteName?: string;
  currentPhoneNumber: PhoneNumber;
  callRoutes: CallRoute[];
  remainingPhoneNumbers?: PhoneNumber[];
  isLoading: boolean;
  handleReassignPhoneNumber: (newCallRouteId: string, reassignAll: boolean) => void;
  onClose: () => void;
}) => {
  const { t } = useTranslation('phone');
  const { getScopeName } = useAppScopeStore();
  const { settingsTenantLocation } = usePhoneSettingsShallowStore('settingsTenantLocation');
  const isUnify = settingsTenantLocation?.locationType === 'unify';

  const [hasConfirmed, setHasConfirmed] = useState(false);

  const { getFieldProps, values } = useForm({
    fields: {
      phoneNumber: {
        type: 'phone',
      },
      callRouteId: {
        value: UNASSIGNED_CALL_ROUTE_ID,
        type: 'dropdown',
      },
      reassignAll: {
        value: false,
        type: 'checkbox',
      },
    },
  });

  const formattedPhoneNumbersText = useMemo(() => {
    const formattedNumbers = remainingPhoneNumbers.reduce((acc, num, index) => {
      // Format the phone number and add it to the accumulator separated by a comma if there is another number, else
      // use a period.
      const formattedNumber = formatPhoneNumber(String(num.phoneNumber.nationalNumber));
      return index === remainingPhoneNumbers.length - 1 ? `${acc} ${formattedNumber}.` : `${acc} ${formattedNumber},`;
    }, t('Phone Numbers:'));

    return formattedNumbers;
  }, [remainingPhoneNumbers, t]);

  const dropdownOptions = useMemo(() => {
    if (isUnify) {
      return callRoutes.map((route) => ({
        value: route.callRouteId,
        // If the location is Unify, we want to include the name of the location as part of the label if it exists.
        label: route.locationId ? `${route.name} (${getScopeName(route.locationId)})` : route.name,
      }));
    }

    return callRoutes.map((route) => ({
      value: route.callRouteId,
      label: route.name,
    }));
  }, [callRoutes, isUnify, getScopeName]);

  return (
    <>
      {/*
        Display the confirmation modal to ask user to confirm that they need to first reassign
        their phone numbers before deleting the call route.
      */}
      {!hasConfirmed && (
        <ReassignConfirmationModal
          callRouteName={callRouteName ?? ''}
          onClose={onClose}
          onConfirm={() => {
            setHasConfirmed(true);
          }}
        />
      )}

      {hasConfirmed && (
        <>
          <Modal.Header>{t('Reassign Phone Numbers')}</Modal.Header>
          <Modal.Label>
            {t('{{count}} phone numbers remaining', {
              count: remainingCount,
            })}
          </Modal.Label>
          <Modal.Body
            css={css`
              padding: 0 ${theme.spacing(3)};
            `}
          >
            <div
              css={css`
                display: flex;
                flex-direction: column;
                width: 318px;
                gap: ${theme.spacing(2)};
              `}
            >
              <Text
                css={css`
                  margin-bottom: ${theme.spacing(2)};
                `}
              >
                {t('Reassign your phone number below.')}
              </Text>
              <TextField
                label={t('Phone Number')}
                {...getFieldProps('phoneNumber')}
                readOnly
                value={formatPhoneNumber(String(currentPhoneNumber?.phoneNumber.nationalNumber))}
              />

              <DropdownField label={t('New Route')} {...getFieldProps('callRouteId')}>
                <DropdownField.Option value={UNASSIGNED_CALL_ROUTE_ID}>
                  <Text>{t('Unassign (No Call Route)')}</Text>
                </DropdownField.Option>
                {dropdownOptions?.map((route) => (
                  <DropdownField.Option key={route.value} value={route.value}>
                    {route.label}
                  </DropdownField.Option>
                ))}
              </DropdownField>
            </div>

            {remainingCount > 1 && (
              <div
                css={css`
                  display: flex;
                  flex-direction: column;
                  gap: ${theme.spacing(1)};
                  min-height: 66px;
                `}
              >
                <CheckboxField {...getFieldProps('reassignAll')} label={t('Reassign all numbers to this route')} />
                {!!values.reassignAll && (
                  <Text size='small' color='subdued'>
                    {formattedPhoneNumbersText}
                  </Text>
                )}
              </div>
            )}
          </Modal.Body>
          <Modal.Footer
            primary={{
              label: 'Reassign and Continue',
              disabled: !values.callRouteId || isLoading,
              trackingId: trackingId({
                context: 'setting',
                feature: 'call-routes',
                details: 'delete::reassign-btn',
              }),
              onClick: () => {
                if (!values.callRouteId) {
                  logError('No call route selected for reassignment', 'ReassignPhoneNumber:onClick', {
                    callRouteName,
                    reassignAll: values.reassignAll,
                  });
                  return;
                }
                handleReassignPhoneNumber(values.callRouteId, values.reassignAll ?? false);
              },
            }}
            secondary={{
              label: 'Cancel',
              disabled: isLoading,
              trackingId: trackingId({
                context: 'setting',
                feature: 'call-routes',
                details: 'delete::reassign-cancel-btn',
              }),
              onClick: onClose,
            }}
          />
        </>
      )}
    </>
  );
};

const ReassignConfirmationModal = ({
  callRouteName,
  onClose,
  onConfirm,
}: {
  callRouteName: string;
  onClose: () => void;
  onConfirm: () => void;
}) => {
  const { t } = useTranslation('phone');

  return (
    <>
      <Modal.Header>{t('Cannot Delete Call Route')}</Modal.Header>
      <Modal.Body>
        <Text>
          {t('In order to delete {{callRouteName}}, you must first reassign all of the associated phone numbers.', {
            callRouteName,
          })}
        </Text>
        <Text>{t('Do you want to reassign your associated phone numbers?')}</Text>
      </Modal.Body>
      <Modal.Footer
        primary={{
          label: 'Yes, Reassign',
          trackingId: trackingId({
            context: 'setting',
            feature: 'call-routes',
            details: 'delete::reassign-confirmation-btn',
          }),
          onClick: onConfirm,
        }}
        secondary={{
          label: 'No, Cancel',
          trackingId: trackingId({
            context: 'setting',
            feature: 'call-routes',
            details: 'delete::reassign-confirmation-cancel-btn',
          }),
          onClick: onClose,
        }}
      />
    </>
  );
};

// Helper function to format a raw phone number string into (xxx)-xxx-xxxx
// NOTE: it's important to that the phone numbers are formatted without any spaces so that the line-clamp css rule
// does not break the phone number incorrectly. [(xxx) xxx-xxxx will not work but (xxx)-xxx-xxxx will work]
const formatSinglePhoneNumber = (numStr: string) => {
  // Remove all non-digit characters
  const cleaned = ('' + numStr).replace(/\D/g, '');
  // Match and format if it's a 10-digit number
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    return `(${match[1]})-${match[2]}-${match[3]}`;
  }
  // Return original string if it doesn't match the expected format
  return numStr;
};

export const SuccessMessageDisplay = ({ phoneNumbers = [] }: { phoneNumbers: PhoneNumber[] }) => {
  const { t } = useTranslation('phone');

  const displayText = useMemo(() => {
    const count = phoneNumbers.length;
    const suffix = t(' successfully reassigned');

    if (count === 0) {
      return t(`Phone number(s) successfully reassigned`);
    }

    // Format all numbers in the array first.
    const formattedNumbers = phoneNumbers.map((number) =>
      formatSinglePhoneNumber(String(number.phoneNumber.nationalNumber))
    );

    let numberPart;

    // Rule: Display a single number.
    // Example: (555)-123-4567 successfully reassigned
    if (count === 1) {
      numberPart = formattedNumbers[0];
    }
    // Rule: For two numbers, separate with "and".
    // Example: (555)-123-4567 and (555)-987-6543 successfully reassigned
    else if (count === 2) {
      numberPart = `${formattedNumbers[0]} ${t('and')} ${formattedNumbers[1]}`;
    }
    // Rule: For three numbers, use commas and "and".
    // Example: (555)-123-4567, (555)-987-6543, and (555)-111-2222 successfully reassigned
    else if (count === 3) {
      numberPart = `${formattedNumbers.slice(0, 2).join(', ')} ${t('and')} ${formattedNumbers[2]}`;
    }
    // Rule: For more than three, show the first three, then "+ N".
    // Example: (555)-123-4567, (555)-987-6543, (555)-111-2222 + 2 successfully reassigned
    else {
      const firstThree = formattedNumbers.slice(0, 3).join(', ');
      const remainingCount = count - 3;
      numberPart = `${firstThree} + ${remainingCount}`;
    }

    return `${numberPart}${suffix}`;
  }, [phoneNumbers, t]);

  return (
    <>
      <Text
        size='medium'
        css={css`
          display: -webkit-box;
          -webkit-box-orient: vertical;
          // truncates the text to a maximum of 2 lines
          -webkit-line-clamp: 2;
          overflow: hidden;
        `}
      >
        {displayText}
      </Text>
    </>
  );
};
